import React, { useState, useEffect, useMemo } from "react";
import { useStepNavigate } from "../hooks/useStepNavigate";
import { useOrder } from "../context/OrderContext";
import { useOrderSave } from "../context/OrderSaveContext";
import { useStaticOptions } from "../context/StaticOptionsContext";
import TablerLayout from "../components/layout/TablerLayout";
import StepperNavButton from "../components/stepper/StepperNavButton";
import { showErrorToast, showSuccessToast } from "../components/toasts/ToastService";
import ColorPicker, { normalizeShoeColorOptions } from "../components/form_elements/ColorPicker";

const slugify = (s) =>
    String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9áéíóúüűöő\s-]/g, "")
        .trim()
        .replace(/\s+/g, "_");

/**
 * Többszínű cipő: legfeljebb 3 színcsoport, a csoportok alkatrészeit a modell admin konfigja (color_groups) adja.
 * Mindhárom csoportban az összes élő szín×bőr opció választható (Elit kérése), csoportonként saját keresővel.
 */
const TobbSzinu = () => {
    const { order, updateOrder } = useOrder();
    const navigate = useStepNavigate();
    const { saveOrder } = useOrderSave();
    const { staticOptions, loading: optionsLoading } = useStaticOptions();

    const colorsAndMaterials = order.model_options?.colors_and_materials_for_model || null;

    const shoe_colors = useMemo(
        () => normalizeShoeColorOptions(staticOptions?.shoe_color_options),
        [staticOptions?.shoe_color_options]
    );

    const [selectedColors, setSelectedColors] = useState({});

    const error = !colorsAndMaterials
        ? "Hiányzik a modellhez tartozó színinformáció (colors_and_materials_for_model)."
        : null;

    const { categories, categoryParts } = useMemo(() => {
        if (!colorsAndMaterials) {
            return { categories: [], categoryParts: {} };
        }

        const groupKeys = Object.keys(colorsAndMaterials)
            .filter((k) => k.startsWith("color_group_"))
            .sort((a, b) => {
                const na = parseInt(a.replace("color_group_", ""), 10) || 0;
                const nb = parseInt(b.replace("color_group_", ""), 10) || 0;
                return na - nb;
            });

        const categoriesTmp = groupKeys.map((gk, idx) => ({
            key: gk,
            title: `Szín ${idx + 1}`,
            index: idx + 1,
        }));

        const categoryPartsTmp = groupKeys.reduce((acc, gk) => {
            const parts = Array.isArray(colorsAndMaterials[gk]) ? colorsAndMaterials[gk] : [];
            acc[gk] = parts.map((p) => ({ key: slugify(p), label: p }));
            return acc;
        }, {});

        return {
            categories: categoriesTmp,
            categoryParts: categoryPartsTmp,
        };
    }, [colorsAndMaterials]);

    // csak azok a csoportok látszanak, amelyekhez a modellen alkatrész van rendelve
    const visibleCategories = useMemo(
        () => categories.filter((cat) => (categoryParts[cat.key] || []).length > 0 && shoe_colors.length > 0),
        [categories, categoryParts, shoe_colors]
    );

    // a rendelésben tárolt színek visszatöltése
    useEffect(() => {
        if (!visibleCategories.length) return;

        const storedIds = [
            order.hmeliorder?.hmeliorder_color_1 ?? null,
            order.hmeliorder?.hmeliorder_color_2 ?? null,
            order.hmeliorder?.hmeliorder_color_3 ?? null,
        ];

        // tartalék: a tárolt szöveg ("S9 Fekete / Barkás, B21 Sötétzöld / Boksz") részei, ha az azonosítók nincsenek meg
        const storedTexts = String(order.hmeliorder?.hmeliorder_leather_color || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);

        const mapped = {};
        visibleCategories.forEach((cat, idx) => {
            const storedId = Number(storedIds[idx]) || null;
            const byId = storedId && storedId > 1 ? shoe_colors.find((color) => color.id === storedId) : null;
            const byText = !byId && storedTexts[idx]
                ? shoe_colors.find((color) => color.text === storedTexts[idx] || color.label === storedTexts[idx])
                : null;
            const match = byId || byText;
            if (match) {
                mapped[cat.key] = match.id;
            }
        });

        setSelectedColors((prev) => {
            const same =
                Object.keys(prev).length === Object.keys(mapped).length &&
                Object.keys(prev).every((key) => prev[key] === mapped[key]);
            return same ? prev : mapped;
        });
    }, [
        order.hmeliorder?.hmeliorder_color_1,
        order.hmeliorder?.hmeliorder_color_2,
        order.hmeliorder?.hmeliorder_color_3,
        order.hmeliorder?.hmeliorder_leather_color,
        visibleCategories,
        shoe_colors,
    ]);

    const buildLeatherColorValue = (selectionMap) =>
        visibleCategories
            .map((cat) => shoe_colors.find((color) => color.id === selectionMap[cat.key])?.text || "")
            .join(", ");

    const applySelection = (selectionMap) => {
        const selectedIdsByCategory = visibleCategories.map((cat) => Number(selectionMap[cat.key]) || null);

        const currentIds = [
            order.hmeliorder?.hmeliorder_color_1 ?? null,
            order.hmeliorder?.hmeliorder_color_2 ?? null,
            order.hmeliorder?.hmeliorder_color_3 ?? null,
        ];

        const sameIds = currentIds.every((id, index) => id === (selectedIdsByCategory[index] ?? null));
        if (sameIds) return;

        updateOrder({
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: buildLeatherColorValue(selectionMap),
                hmeliorder_color_1: selectedIdsByCategory[0] ?? null,
                hmeliorder_color_2: selectedIdsByCategory[1] ?? null,
                hmeliorder_color_3: selectedIdsByCategory[2] ?? null,
            },
        });
    };

    const handleColorSelect = (catKey, color) => {
        const updated = { ...selectedColors, [catKey]: color.id };
        setSelectedColors(updated);
        applySelection(updated);
    };

    const handleBackClick = () => {
        navigate("/EgySzinu");
    };

    const canProceed =
        visibleCategories.length > 0 &&
        visibleCategories.every((cat) => {
            const selected = selectedColors[cat.key];
            return selected !== null && selected !== undefined && selected !== "";
        });

    const handleSubmit = async () => {
        if (!canProceed) {
            showErrorToast("Minden színcsoporthoz válasszon színt!");
            return;
        }

        const payload = {
            ...order,
            hmeliorder: {
                ...order.hmeliorder,
                hmeliorder_leather_color: buildLeatherColorValue(selectedColors),
            },
        };

        updateOrder(payload);

        const result = await saveOrder(payload);
        if (result.success) {
            showSuccessToast("A rendelés sikeresen mentve!");
            navigate("/TuzesSzine");
        } else {
            showErrorToast("Hiba történt a rendelés mentésekor!");
        }
    };

    if (optionsLoading) {
        return (
            <TablerLayout title="Többszínű cipő – színek kiválasztása">
                <div className="text-center">Betöltés...</div>
            </TablerLayout>
        );
    }

    if (error) {
        return (
            <TablerLayout title="Többszínű cipő – színek kiválasztása">
                <div className="text-danger text-center">Hiba történt a színek betöltésekor: {error}</div>
                <div className="d-flex justify-content-start mt-4">
                    <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                </div>
            </TablerLayout>
        );
    }

    return (
        <TablerLayout title="Többszínű cipő – színek kiválasztása">
            <div className="row justify-content-center">
                <div className="col-md-10 mb-4">
                    <div className="card-body">
                        {visibleCategories.length === 0 && (
                            <div className="alert alert-warning text-center">
                                Ehhez a modellhez nincs beállítva színcsoport (alkatrész felosztás) az admin felületen.
                            </div>
                        )}
                        <div className="row g-4 mb-4">
                            {visibleCategories.map((cat, idx) => (
                                <div key={cat.key} className="col-12 col-md-4">
                                    <div className="h-100 d-flex flex-column">
                                        <div className="mb-2">
                                            <h6 className="mb-1 text-center">{cat.title}</h6>
                                        </div>

                                        <div className="mb-3">
                                            {(categoryParts[cat.key] || []).map((part) => (
                                                <div
                                                    key={part.key}
                                                    style={{
                                                        padding: "10px",
                                                        marginBottom: "6px",
                                                        backgroundColor: "white",
                                                        border: "1px solid #e2e8f0",
                                                        fontSize: "15px",
                                                        fontWeight: "500",
                                                        color: "black",
                                                        justifyContent: "center",
                                                        textAlign: "center",
                                                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                                                    }}
                                                >
                                                    {part.label}
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ marginTop: "auto" }}>
                                            <ColorPicker
                                                colors={shoe_colors}
                                                selectedId={selectedColors[cat.key] ?? null}
                                                onSelect={(color) => handleColorSelect(cat.key, color)}
                                                placeholder="Kód vagy név (pl. B9)"
                                                autoFocus={idx === 0}
                                                maxHeight={300}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="card-footer d-flex justify-content-between mt-4">
                        <StepperNavButton side="left" label="Vissza" onClick={handleBackClick} />
                        <StepperNavButton side="right" label="Tovább" onClick={handleSubmit} disabled={!canProceed} />
                    </div>
                </div>
            </div>
        </TablerLayout>
    );
};

export default TobbSzinu;
